<template>
  <UIDialog
    :is-open
    :title
    :z-index
    size="2md"
    @close="handleClose"
  >
    <template v-if="returnable" #title>
      <UIButtonBack label="Back" @click="handleClickBack" />
      <span>{{ title }}</span>
    </template>
    <UITabs v-model="tab" :tabs />
    <template v-if="tab === RepositoryTabs.DATA">
      <RepositoryDataForm
        ref="dataFormRef"
        v-model="repository"
        :loading
        @save="handleUpdate"
        @close="handleClose"
      >
        <RepositoryPricePrecedence
          :repository
          :disabled="loading || isReadonly"
          @change="handleUpdatePricePrecedence"
        />
        <template #buttons>
          <RepositoryAnalytics
            :repository-id
            :disabled="loading"
            class="mr-auto"
          />
        </template>
      </RepositoryDataForm>
    </template>
    <template v-if="tab === RepositoryTabs.COLLABORATORS">
      <RepositoryCollaborators :repository />
    </template>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, markRaw, ref, useTemplateRef, watch, watchEffect } from 'vue'

import { ModalClass, Repository } from '@types'
import { RepositoryTabs } from './utils/enums'

import { ACCESS_TYPE_READONLY, NOTIFICATION_DELAY } from '@/const'

import { useHotKeys } from '@/plugins/hotKeys'

import { handleCatchedError } from '@/helpers/common'
import { NotificationInstance, useNotifications } from '@/plugins/notification'

import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'

import Repositories from '../List/Repositories.vue'
import RepositoryAnalytics from './components/Analytics.vue'
import RepositoryCollaborators from './components/Collaborators.vue'
import RepositoryDataForm from './components/DataForm.vue'
import RepositoryPricePrecedence from './components/PricePrecedence.vue'

import { UIDialog } from '@ui/modals'
import { UITabs } from '@ui/core'
import { UIButtonBack } from '@ui/buttons'

type Props = {
  modal: ModalClass<any>
  repository: Repository
  returnable?: boolean
}
const props = defineProps<Props>()

const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()

const { progress, remove, update } = useNotifications()

const dataFormRef = useTemplateRef('dataFormRef')

const tab = ref<RepositoryTabs>(RepositoryTabs.DATA)
const repository = ref<Repository>(props.repository)
const loading = ref(false)

const isCollaboratorsInited = ref(false)

const repositoryId = computed(() => repository.value.id)

const tabs = computed(() =>
  Object.values(RepositoryTabs).map(name => ({
    name,
  })),
)

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const title = computed(() => `${props.repository.name} repository settings`)

const isReadonly = computed(
  () =>
    props.repository.settings?.readonly ||
    !props.repository.user_repo_settings?.access_type ||
    ACCESS_TYPE_READONLY === props.repository.user_repo_settings?.access_type,
)

const handleUpdate = async (values: Partial<Repository>) => {
  let nid: NotificationInstance | undefined
  loading.value = true
  try {
    nid = await progress({
      message: 'Updating repository',
    })
    if (
      repositoriesStore.getList.find(
        repo =>
          repo.name.toLowerCase() === values.name?.toLowerCase() &&
          repo.id !== repositoryId.value,
      )
    ) {
      await update(nid, {
        type: 'error',
        message:
          'This name of the repository already exists. Try something else',
      })
      loading.value = false
      return
    }
    const createdRepo = await repositoriesStore.update(values)
    dataFormRef.value?.resetForm(values as any)
    nid &&
      (await update(
        nid,
        {
          type: 'success',
          message: `Repository ${createdRepo?.name} updated`,
        },
        NOTIFICATION_DELAY,
      ))
  } catch (e) {
    nid && (await remove(nid))
    handleCatchedError(e as string, values)
  } finally {
    loading.value = false
  }
}

const handleUpdatePricePrecedence = async (values: string[]) => {
  dataFormRef.value?.updatePriceSourcePrecedence(values)
}

const handleClickBack = () => {
  const modalInstance = modalsStore.init('repositories', markRaw(Repositories))
  modalInstance?.open(modalsStore.getZIndex(), {})
  handleClose()
}

const handleClose = () => {
  props.modal.close()
}

watch(isOpen, value => {
  if (!value) return
  tab.value = RepositoryTabs.DATA
  isCollaboratorsInited.value = false
})

watchEffect(async () => {
  if (
    !repositoryId.value ||
    tab.value !== RepositoryTabs.COLLABORATORS ||
    isCollaboratorsInited.value
  )
    return
  try {
    await repositoriesCollaboratorsStore.fetch(repositoryId.value)
    isCollaboratorsInited.value = true
  } catch (e) {
    handleCatchedError(e as string, { repositoryId: repositoryId.value })
  }
})

useHotKeys('escape', handleClose, { flag: isOpen })
</script>

<style scoped>
:deep(.ui-dialog__title) {
  @apply flex items-center;
  @apply gap-4;
  @apply overflow-hidden;

  span {
    @apply flex-auto;
    @apply truncate;
  }
}
</style>
